import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { getAllSlas } from '@/service/sla'
import { getAllTicketCategories } from '../service/ticketCategory'
import { getAllTicketPriorities } from '../service/ticketPriority'
import { getAllTickets, getTicket } from '../service/tickets'
import { getAllTicketStatuses } from '../service/ticketStatus'
import { getAllTicketTypes } from '../service/ticketType'

export const useTicketStore = defineStore('tickets', () => {
  const tickets = ref<any[]>([])

  // Variables de settings
  const priorities = ref<any[]>([])
  const statuses = ref<any[]>([])
  const categories = ref<any[]>([])
  const slas = ref<any[]>([])
  const types = ref<any[]>([])

  // Loading states
  const isLoadingTickets = ref(false)
  const isLoadingMoreTickets = ref(false)
  const isLoadingTicket = ref(false)
  const isLoadingSettings = ref(false)

  // Navigation state
  const hasActiveFilters = ref(false)
  const currentListContext = ref<{
    filters: any
    sortBy: string
    sortOrder: string
    page: number
    search?: string
  } | null>(null)

  // Table update signal for triggering reactivity
  const tableUpdateSignal = ref(0)

  const triggerTableUpdate = () => {
    tableUpdateSignal.value++
  }

  // State of navigation
  const hasNextTicket = ref(false)
  const hasPreviousTicket = ref(false)
  const showTicketNavigation = ref(false)

  const savedAppliedFilters = ref()
  const savedSortOrder = ref<string | null>('desc')
  const savedSortBy = ref<string | null>('createdAt')
  const storeFilterData = computed(() => [...priorities.value, ...statuses.value, ...categories.value, ...types.value])
  const pagination = ref<{
    pageSize: number
    pageIndex: number
    meta: {
      has_more: boolean
      has_prev: boolean
      limit: number
      next_page: number | null
      page: number
      prev_page: number | null
      total_items: number
      total_pages: number
    } | null
  }>({
    pageSize: 10,
    pageIndex: 0,
    meta: null,
  })
  const totalItems = ref(0)

  const openTickets = computed(() => tickets.value.filter((ticket) => ticket.customData?.isOpen))
  const closedTickets = computed(() => tickets.value?.filter((ticket) => !ticket.customData?.isOpen))

  const fetchTickets = async ({
    limit,
    page,
    statusId,
    search,
    sortBy,
    sortOrder,
    ...extraQueryParams
  }: {
    limit?: number
    page?: number | null | undefined
    statusId?: string
    search?: string
    sortBy?: string | undefined
    sortOrder?: string
    [key: string]: any
  } = {}) => {
    const route = useRoute()

    isLoadingTickets.value = true

    const hasFilters = !!(statusId || search || route?.query.search || Object.keys(extraQueryParams).length > 0)

    hasActiveFilters.value = hasFilters

    if (hasFilters) {
      currentListContext.value = {
        filters: extraQueryParams,
        sortBy: sortBy || savedSortBy.value || 'createdAt',
        sortOrder: sortOrder || savedSortOrder.value || 'desc',
        page: page || 1,
        search: search || (route?.query.search as string | undefined),
      }
    } else {
      currentListContext.value = null
      hasNextTicket.value = false
      hasPreviousTicket.value = false
    }

    if (extraQueryParams) savedAppliedFilters.value = extraQueryParams
    if (sortOrder) savedSortOrder.value = sortOrder
    if (sortBy) {
      savedSortBy.value = sortBy
    } else {
      savedSortBy.value = 'createdAt'
    }
    const { onSuccess, onError } = getAllTickets({
      limit: limit ?? pagination.value.pageSize,
      page,
      statusId,
      search: (route?.query.search as string | undefined) ?? search,
      sortBy: (route?.query.sortBy as string | undefined) ?? sortBy,
      sortOrder: (route?.query.sortOrder as string | undefined) ?? sortOrder,
      ...Object.entries(extraQueryParams)
        .filter(([key]) => !['dateRange', 'search', 'sortBy', 'sortOrder'].includes(key))
        .map(([key, value]) => (key.endsWith('Id') ? [`${key}[in]`, value] : [`${key}Id[in]`, value]))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
      ...(extraQueryParams.dateRange && { dateRange: extraQueryParams.dateRange }),
    })

    return new Promise<void>((resolve) => {
      onSuccess((response) => {
        tickets.value = response.data
        pagination.value.meta = response.meta

        if (hasActiveFilters.value && currentListContext.value) {
          hasNextTicket.value = response.meta?.has_more ?? false
          hasPreviousTicket.value = response.meta?.has_prev ?? false
        }

        if ((!route?.query || !Object.entries(route?.query)?.length) && !search && !Object.entries(extraQueryParams)?.length)
          totalItems.value = response.meta?.total_items ?? 0
        isLoadingTickets.value = false
        resolve(response.data)
      })
      onError(() => {
        isLoadingTickets.value = false
        hasNextTicket.value = false
        hasPreviousTicket.value = false
        resolve()
      })
    })
  }

  const fetchSettings = async () => {
    isLoadingSettings.value = true
    try {
      const fetchPriority = () =>
        new Promise<any>((resolve) => {
          const { onSuccess } = getAllTicketPriorities()

          onSuccess((data) => resolve(data))
        })
      const fetchStatus = () =>
        new Promise<any>((resolve) => {
          const { onSuccess } = getAllTicketStatuses()

          onSuccess((data) => resolve(data))
        })
      const fetchCategory = () =>
        new Promise<any>((resolve) => {
          const { onSuccess } = getAllTicketCategories()

          onSuccess((data) => resolve(data))
        })
      const fetchType = () =>
        new Promise<any>((resolve) => {
          const { onSuccess } = getAllTicketTypes()

          onSuccess((data) => resolve(data))
        })
      const fetchSla = () =>
        new Promise<any>((resolve) => {
          const { onSuccess } = getAllSlas()

          onSuccess((data) => resolve(data))
        })

      const [priorityData, statusData, categoryData, typeData, slaData] = await Promise.all([
        fetchPriority(),
        fetchStatus(),
        fetchCategory(),
        fetchType(),
        fetchSla(),
      ])

      priorities.value = priorityData.map((priorityObject: any) => ({ ...priorityObject, accessorKey: 'priority' }))
      statuses.value = statusData.map((statusObject: any) => ({ ...statusObject, accessorKey: 'status' }))
      categories.value = categoryData.map((categoryObject: any) => ({ ...categoryObject, accessorKey: 'category' }))
      types.value = typeData.map((typeObject: any) => ({ ...typeObject, accessorKey: 'type' }))
      slas.value = slaData
    } finally {
      isLoadingSettings.value = false
    }
  }

  const addTicket = (ticket: any) => {
    tickets.value.unshift(ticket)
  }

  const setTickets = async (newTickets: { id: string; title: string; customData: any }[]) => {
    tickets.value = newTickets
  }

  const setIsLoadingTickets = (isLoading: boolean) => {
    isLoadingTickets.value = isLoading
  }

  const setSavedSortBy = (sortBy: string | null) => {
    savedSortBy.value = sortBy
  }

  const setSavedSortOrder = (sortOrder: string | null) => {
    savedSortOrder.value = sortOrder
  }

  const getTicketById = async (id: string) => {
    isLoadingTicket.value = true
    const { onSuccess, onError } = getTicket(id)

    return new Promise<void>((resolve) => {
      onSuccess((data) => {
        isLoadingTicket.value = false
        const existingTicket = tickets.value.find((ticket) => ticket.id === data.id)

        if (!existingTicket) {
          tickets.value.unshift(data)
        }
        resolve(data)
      })
      onError(() => {
        isLoadingTicket.value = false
        resolve()
      })
    })
  }

  function patchTicket(ticketId: string, updateFn: (t: any) => any) {
    tickets.value = tickets.value.map((t) => (t.id === ticketId ? updateFn(t) : t))
  }

  const removeSla = (ticketId: string) => {
    patchTicket(ticketId, (t) => ({
      ...t,
      response_time: null,
      resolution_time: null,
      deadline_response_time: null,
      deadline_resolution_time: null,
    }))
  }

  const updateTicket = (ticketData: any) => {
    const existingTicket = tickets.value.find((t) => t.id === ticketData.id)

    if (existingTicket) {
      Object.assign(existingTicket, ticketData)
    } else {
      tickets.value.push(ticketData)
    }

    // Trigger table update for reactivity
    triggerTableUpdate()
  }

  const updateTicketSla = (ticketId: string, goalType: 'response' | 'resolution', sla: { slaGoalId: string }) => {
    patchTicket(ticketId, (t) =>
      t[goalType].id === sla.slaGoalId
        ? {
            ...t,
            [`deadline_${goalType}`]: sla,
          }
        : t,
    )
  }
  const clearTickets = () => {
    tickets.value = []
    hasNextTicket.value = false
    hasPreviousTicket.value = false
  }

  const setPagination = ({ pageSize, pageIndex }: { pageSize: number | undefined; pageIndex: number | undefined }) => {
    if (pageSize !== null && typeof pageSize !== 'undefined') pagination.value.pageSize = pageSize
    if (pageIndex !== null && typeof pageIndex !== 'undefined') pagination.value.pageIndex = pageIndex
  }

  const getCurrentTicketIndex = (ticketId: string) => {
    return tickets.value.findIndex((ticket) => ticket.id === ticketId)
  }

  const getNextTicket = async (currentTicketId: string) => {
    if (!currentListContext.value) return null

    const currentIndex = getCurrentTicketIndex(currentTicketId)

    if (currentIndex === -1) return null

    if (currentIndex < tickets.value.length - 1) {
      return tickets.value[currentIndex + 1]
    }

    if (pagination.value.meta?.has_more && currentListContext.value) {
      const nextPage = pagination.value.meta.page + 1

      await fetchTickets({
        page: nextPage,
        limit: pagination.value.pageSize,
        ...currentListContext.value.filters,
        sortBy: currentListContext.value.sortBy,
        sortOrder: currentListContext.value.sortOrder,
        search: currentListContext.value.search,
      })

      return tickets.value[0]
    }

    return null
  }

  const getPreviousTicket = async (currentTicketId: string) => {
    if (!currentListContext.value) return null

    const currentIndex = getCurrentTicketIndex(currentTicketId)

    if (currentIndex === -1) return null

    if (currentIndex > 0) {
      return tickets.value[currentIndex - 1]
    }

    if (pagination.value.meta?.has_prev && currentListContext.value) {
      const prevPage = pagination.value.meta.page - 1

      await fetchTickets({
        page: prevPage,
        limit: pagination.value.pageSize,
        ...currentListContext.value.filters,
        sortBy: currentListContext.value.sortBy,
        sortOrder: currentListContext.value.sortOrder,
        search: currentListContext.value.search,
      })

      const lastIndex = tickets.value.length - 1

      return tickets.value[lastIndex]
    }

    return null
  }

  const updateNavigationState = (currentTicketId: string) => {
    if (!currentListContext.value) {
      hasNextTicket.value = false
      hasPreviousTicket.value = false
      showTicketNavigation.value = false

      return
    }

    const currentIndex = getCurrentTicketIndex(currentTicketId)

    if (currentIndex === -1) {
      hasNextTicket.value = false
      hasPreviousTicket.value = false
      showTicketNavigation.value = false

      return
    }

    hasNextTicket.value = currentIndex < tickets.value.length - 1 || (pagination.value.meta?.has_more ?? false)

    hasPreviousTicket.value = currentIndex > 0 || (pagination.value.meta?.has_prev ?? false)
    showTicketNavigation.value = hasNextTicket.value || hasPreviousTicket.value
  }

  return {
    tickets,
    openTickets,
    closedTickets,
    fetchTickets,
    addTicket,
    setTickets,
    getTicketById,
    updateTicket,
    clearTickets,
    updateTicketSla,
    removeSla,
    priorities,
    statuses,
    categories,
    types,
    slas,
    fetchSettings,
    pagination,
    setPagination,
    isLoadingTickets,
    isLoadingMoreTickets,
    setIsLoadingTickets,
    isLoadingSettings,
    storeFilterData,
    savedAppliedFilters,
    savedSortOrder,
    setSavedSortOrder,
    savedSortBy,
    setSavedSortBy,
    totalItems,
    getCurrentTicketIndex,
    getNextTicket,
    getPreviousTicket,
    hasActiveFilters,
    currentListContext,
    hasNextTicket,
    hasPreviousTicket,
    showTicketNavigation,
    updateNavigationState,
    tableUpdateSignal,
    triggerTableUpdate,
  }
})
